// src/core/predicciones.js — capa de datos de las predicciones del Prode.
//
// FASE 4 · F1.3.c2. La tabla `predicciones` vive en Supabase Postgres (cierra
// DT-R). Cada predicción es única por `(player_id, partido_id)` — el Prode es
// por jugador, es decir por grupo (un usuario en 2 grupos tiene 2 predicciones
// separadas para el mismo partido).
//
// Lecturas: SELECT directo. La RLS de `predicciones` permite ver las propias
// siempre; las ajenas solo si el partido está `live`/`finished` (anti-trampa:
// no se revelan las predicciones del grupo antes del partido).
//
// Escritura: pasa por el RPC `guardar_prediccion` (`SECURITY DEFINER`) — la
// RLS sola NO valida que el partido esté PROGRAMADO; el RPC sí (lock-by-status
// server-side), además de validar el clasificado y el flag `locked`.
//
// Forma de la predicción en la DB:
//   - group_match  → score_local/score_visitante codifican el resultado:
//     1-0 = gana local, 0-0 = empate, 0-1 = gana visitante. La tabla no tiene
//     columna de resultado (discrepancia vs spec §11 — ver reporte F1.3.c2).
//   - playoff_match → score_local/score_visitante = goles; clasificado_codigo
//     = equipo que pasa por penales (obligatorio si la predicción es empate).

import { supabase } from '../supabase.js'

// FASE 4 · DT-BJ (parpadeo Prode): cache en memoria con TTL 60s + dedup de
// requests en vuelo (mismo patrón `memoConTTL` que core/rituales.js y
// core/tribuna.js). Evita refetch del set de predicciones del jugador en
// navegación corta. Se invalida al guardar (upsertPrediccion abajo).

const PREDICCIONES_CACHE_TTL_MS = 60_000

const memoConTTL = (ttl) => {
  const cache = new Map()
  const inflight = new Map()
  return {
    run: async (clave, fn) => {
      const hit = cache.get(clave)
      if (hit && Date.now() - hit.at < ttl) return hit.value
      if (inflight.has(clave)) return inflight.get(clave)
      const promise = (async () => {
        try {
          const value = await fn()
          cache.set(clave, { value, at: Date.now() })
          return value
        } finally {
          inflight.delete(clave)
        }
      })()
      inflight.set(clave, promise)
      return promise
    },
    clear: () => { cache.clear(); inflight.clear() },
  }
}

const _prediccionesMemo = memoConTTL(PREDICCIONES_CACHE_TTL_MS)

export const invalidarCachePredicciones = () => _prediccionesMemo.clear()

// ─── Lecturas (SELECT directo — la RLS filtra la visibilidad) ──────────────

// Historial Prode para el perfil del jugador (spec Prode_Scoring v1.1 §15).
// Devuelve filas con la predicción + datos del partido (fecha, equipos, resultado,
// fase, es_tercer_puesto) para renderizar la tabla Fecha/Partido/Fase/Predicción/Resultado/Puntos/Acumulado.
// Ordenado por fecha_hora ASC. El "Acumulado" lo calcula el caller (cliente).
export const getHistorialProde = async (playerId) => {
  if (!playerId) return []
  const { data, error } = await supabase
    .from('predicciones')
    .select(`
      prediction_id, prediction_type, score_local, score_visitante,
      clasificado_codigo, puntos_obtenidos, created_at,
      partidos!inner (
        partido_id, match_number, fecha_hora, fase, grupo_mundial,
        es_tercer_puesto, status, status_detail,
        equipo_local_codigo, equipo_local_nombre, equipo_local_bandera_url,
        equipo_visitante_codigo, equipo_visitante_nombre, equipo_visitante_bandera_url,
        goles_local, goles_visitante, ganador_penales_codigo,
        viene_local_de, viene_visitante_de
      )
    `)
    .eq('player_id', playerId)
    .order('fecha_hora', { foreignTable: 'partidos', ascending: true })
  if (error) { console.error('[predicciones] getHistorialProde:', error.message); return [] }
  // Tercer puesto: excluido del producto (UI + scoring). Filtrarlo acá lo
  // oculta de todos los tabs del perfil (Mi Mundial / Análisis / Evolución).
  return (data || [])
    .filter((row) => !row.partidos?.es_tercer_puesto)
    .map((row) => ({
      prediction_id: row.prediction_id,
      prediction_type: row.prediction_type,
      score_local: row.score_local,
      score_visitante: row.score_visitante,
      clasificado_codigo: row.clasificado_codigo,
      puntos_obtenidos: row.puntos_obtenidos,
      created_at: row.created_at,
      partido: row.partidos,
    }))
}

// Todas las predicciones de un jugador.
export const getPredicciones = async (playerId) => {
  if (!playerId) return []
  return _prediccionesMemo.run(playerId, async () => {
    const { data, error } = await supabase
      .from('predicciones')
      .select('*')
      .eq('player_id', playerId)
    if (error) { console.error('[predicciones] getPredicciones:', error.message); return [] }
    return data || []
  })
}

// La predicción de un jugador para un partido (o null si no jugó).
export const getPrediccionPartido = async (playerId, partidoId) => {
  if (!playerId || !partidoId) return null
  const { data, error } = await supabase
    .from('predicciones')
    .select('*')
    .eq('player_id', playerId)
    .eq('partido_id', partidoId)
    .maybeSingle()
  if (error) { console.error('[predicciones] getPrediccionPartido:', error.message); return null }
  return data || null
}

// Predicciones de todos los jugadores de un grupo para un partido. La RLS solo
// devuelve las ajenas si el partido está `live`/`finished` — para un partido
// PROGRAMADO devuelve únicamente la del jugador actual.
export const getPrediccionesGrupo = async (groupId, partidoId) => {
  if (!groupId || !partidoId) return []
  const { data, error } = await supabase
    .from('predicciones')
    .select('*, jugadores!inner(group_id, display_name)')
    .eq('partido_id', partidoId)
    .eq('jugadores.group_id', groupId)
  if (error) { console.error('[predicciones] getPrediccionesGrupo:', error.message); return [] }
  return data || []
}

// ─── Escritura (RPC SECURITY DEFINER — valida partido PROGRAMADO) ──────────

// INSERT/UPDATE atómico de la predicción del jugador para un partido.
// `data` = { prediction_type, score_local, score_visitante, clasificado_codigo }.
// Devuelve { data } | { error }. El RPC rechaza si el partido no está
// PROGRAMADO (`partido_no_programado`) o si la predicción está bloqueada.
export const upsertPrediccion = async (playerId, partidoId, data) => {
  const { data: row, error } = await supabase.rpc('guardar_prediccion', {
    p_player_id: playerId,
    p_partido_id: partidoId,
    p_prediction_type: data.prediction_type,
    p_score_local: data.score_local,
    p_score_visitante: data.score_visitante,
    p_clasificado_codigo: data.clasificado_codigo ?? null,
  })
  if (!error) invalidarCachePredicciones()
  return error ? { error } : { data: row }
}
